import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { selectLanguage, resetSearchPage } from '../redux/Slices/busSlice';
import logo from '../../components/assets/logos/logo2.PNG';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector(selectLanguage);
  const [isMobile, setIsMobile] = useState(false);
  const [hoverStates, setHoverStates] = useState({});

  // Check screen size for responsiveness
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Set language attribute
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // ✅ Reset bus search state whenever the User Dashboard mounts.
  // This ensures Bus Finder starts fresh after returning to dashboard,
  // but preserves search data during Bus Finder ↔ Bus Details navigation.
  useEffect(() => {
    dispatch(resetSearchPage());
  }, [dispatch]);

  // All features as requested
  const features = [
    { 
      id: 1, 
      name: t('busFinder'), 
      icon: '🚌', 
      description: t('findBusDescription', 'Bus Finder and schedules'),
      navigateTo: '/user-bus-finder'
    },
    { 
      id: 6, 
      name: t('aboutTeam', 'About Team'), 
      icon: '👥', 
      description: t('meetOurTeam', 'Meet our team'),
      navigateTo:'/about-team'
    },
    { 
      id: 5, 
      name: t('userGuide', 'User Guide'), 
      icon: '📘', 
      description: t('howToUse', 'How to use the system'),
      navigateTo: '/user-guide'  
    },
  ];

  // System Overview Stats based on images
  const systemStats = [
    { 
      id: 1, 
      value: '1', 
      label: currentLanguage === 'hi' ? 'राज्य' : 'State',
      icon: '🏛️'
    },
    { 
      id: 2, 
      value: '4', 
      label: currentLanguage === 'hi' ? 'ज़िले' : 'Districts',
      icon: '🗺️'
    },
    { 
      id: 3, 
      value: '113', 
      label: currentLanguage === 'hi' ? 'गाँव और शहर' : 'Villages & Cities',
      icon: '🏘️'
    },
    { 
      id: 4, 
      value: '142', 
      label: currentLanguage === 'hi' ? 'बस स्टॉप और बस स्टैंड' : 'Bus stops & bus stands',
      icon: '🚏'
    },
  ];

  const handleFeatureClick = (feature) => {
    if (feature.comingSoon) {
      alert(`${feature.name} ${t('comingSoon', 'coming soon!')}`);
      return;
    }
    
    if (feature.navigateTo) {
      navigate(feature.navigateTo);
    }
  };

  const handleMouseEnter = (id) => {
    setHoverStates(prev => ({ ...prev, [id]: true }));
  };

  const handleMouseLeave = (id) => {
    setHoverStates(prev => ({ ...prev, [id]: false }));
  };

  // Styles
  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      fontFamily: "'Noto Serif Devanagari', serif",
      padding: isMobile ? '10px' : '0',
      overflowX: 'hidden',
    },
    header: {
      background: 'linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%)',
      color: 'black',
      padding: isMobile ? '10px 20px' : '10px 40px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      borderBottom: '3px solid #ffeb3b',
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '10px',
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      textDecoration: 'none',
      color: 'black',
    },
    logoImage: {
      height: isMobile ? '45px' : '60px',
      width: 'auto',
      objectFit: 'contain',
      borderRadius: '8px'
    },
    mainContent: {
      flex: '1',
      padding: isMobile ? '15px' : '20px 40px',
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      boxSizing: 'border-box',
    },
    dashboardGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '15px',
      marginBottom: '30px',
      width: '100%',
      boxSizing: 'border-box',
    },
    featureCard: {
      background: '#fff9c4',
      borderRadius: '12px',
      padding: isMobile ? '15px' : '20px',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '10px' : '20px',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
      border: '2px solid transparent',
      cursor: 'pointer',
      minHeight: isMobile ? '80px' : '100px',
      height: '100%',
      width: '100%',
      boxSizing: 'border-box',
      margin: '0 auto',
      position: 'relative',
    },
    featureIcon: {
      fontSize: isMobile ? '2.2rem' : '2.8rem',
      flexShrink: 0,
      width: isMobile ? '50px' : '60px',
      textAlign: 'center'
    },
    featureContent: {
      flex: '1',
      textAlign: 'left',
      minWidth: 0,
      marginTop: isMobile ? '25px' : '0',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
    },
    featureTitle: {
      margin: '0 0 8px 0',
      color: '#333',
      fontSize: isMobile ? '1.1rem' : '1.3rem',
      fontWeight: 600,
      wordBreak: 'break-word',
    },
    featureDescription: {
      color: '#666',
      fontSize: isMobile ? '0.85rem' : '0.95rem',
      lineHeight: '1.4',
      margin: '0',
      wordBreak: 'break-word',
    },
    // Management Panel Section
    managementSection: {
      textAlign: 'center',
      margin: isMobile ? '20px 0' : '30px 0',
      padding: isMobile ? '20px' : '30px',
      background: 'linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%)',
      borderRadius: '15px',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
      width: '100%',
      boxSizing: 'border-box',
    },
    managementTitle: {
      color: 'black',
      fontSize: isMobile ? '1.4rem' : '1.8rem',
      margin: '0 0 10px 0',
      fontWeight: 600
    },
    managementText: {
      color: 'black',
      fontSize: isMobile ? '0.9rem' : '1rem',
      margin: '0 0 20px 0',
    },
    managementButton: {
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: 'white',
      color: '#764ba2',
      padding: isMobile ? '15px 25px' : '20px 40px',
      borderRadius: '12px',
      textDecoration: 'none',
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: 'bold',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    buttonIcon: {
      fontSize: isMobile ? '1.8rem' : '2rem',
      marginBottom: '8px'
    },
    buttonText: {
      marginBottom: '5px',
    },
    buttonSubtext: {
      fontSize: isMobile ? '0.75rem' : '0.85rem',
      opacity: '0.8',
      fontWeight: 'normal',
    },
    // System Overview Section
    systemOverview: {
      background: 'white',
      padding: isMobile ? '20px' : '30px',
      borderRadius: '15px',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
      marginTop: '30px',
      marginBottom: '0',
      width: '100%',
      boxSizing: 'border-box',
    },
    overviewTitle: {
      textAlign: 'center',
      margin: '0 0 25px 0',
      color: '#2c3e50',
      fontSize: isMobile ? '1.5rem' : '2rem',
      fontWeight: 700,
      letterSpacing: '1px'
    },
    overviewSubtitle: {
      textAlign: 'center',
      color: '#7f8c8d',
      fontSize: isMobile ? '0.9rem' : '1rem',
      marginBottom: '25px',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
    },
    statItem: {
      textAlign: 'center',
      padding: isMobile ? '20px 10px' : '25px 15px',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderRadius: '12px',
      boxShadow: '0 3px 10px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      border: '2px solid transparent'
    },
    statIcon: {
      fontSize: isMobile ? '1.8rem' : '2.2rem',
      display: 'block',
      marginBottom: '8px'
    },
    statValue: {
      display: 'block',
      fontSize: isMobile ? '2rem' : '2.5rem',
      fontWeight: 'bold',
      color: '#2c3e50',
      marginBottom: '5px',
    },
    statLabel: {
      display: 'block',
      color: '#495057',
      fontSize: isMobile ? '0.75rem' : '0.9rem',
      fontWeight: '500',
    },
    footer: {
      background: '#fff9c4',
      color: 'black',
      padding: isMobile ? '15px 20px' : '25px 40px',
      textAlign: 'center',
      marginTop: '40px',
      width: '100%',
      boxSizing: 'border-box',
    },
    footerText: {
      margin: '0 0 10px 0',
      opacity: '0.9',
      fontSize: isMobile ? '0.8rem' : '0.95rem',
    },
    footerLinks: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: isMobile ? '15px' : '25px',
    },
    footerLink: {
      color: 'black',
      textDecoration: 'none',
      opacity: '0.8',
      transition: 'opacity 0.3s ease',
      fontSize: isMobile ? '0.8rem' : '0.9rem',
    },
    // Hover effects
    featureCardHover: {
      transform: 'translateY(-3px)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
      borderColor: '#ffeb3b'
    },
    statItemHover: {
      transform: 'translateY(-5px)',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
      borderColor: '#ffeb3b'
    }
  };

  return (
    <div 
      style={styles.container}
      lang={currentLanguage}
    >
      {/* Header with Logo Only */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logoContainer}>
            <Link to="/" style={styles.logoLink}>
              <img 
                src={logo} 
                alt={t('logoAlt', 'Bus Management System Logo')} 
                style={styles.logoImage}
              />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Dashboard Content */}
      <main style={styles.mainContent}>
        {/* Features Grid */}
        <div style={styles.dashboardGrid}>
          {features.map((feature) => (
            <div 
              key={feature.id} 
              style={{
                ...styles.featureCard,
                ...(hoverStates[feature.id] ? styles.featureCardHover : {})
              }}
              onClick={() => handleFeatureClick(feature)}
              onMouseEnter={() => handleMouseEnter(feature.id)}
              onMouseLeave={() => handleMouseLeave(feature.id)}
            >
              <div style={styles.featureIcon}>{feature.icon}</div>
              <div style={styles.featureContent}>
                <h3 style={styles.featureTitle}>{feature.name}</h3>
                <p style={styles.featureDescription}>{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* System Overview Section */}
        <div style={styles.systemOverview}>
          <h2 style={styles.overviewTitle}>
            {currentLanguage === 'hi' ? 'सिस्टम अवलोकन' : 'SYSTEM OVERVIEW'}
          </h2>
          <p style={styles.overviewSubtitle}>
            {currentLanguage === 'hi' 
              ? 'हमारी बस सूचना प्रणाली के आंकड़े' 
              : 'Our Bus Information System Statistics'}
          </p>
          <div style={styles.statsGrid}>
            {systemStats.map((stat) => (
              <div 
                key={stat.id}
                style={styles.statItem}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = '#ffeb3b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 3px 10px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <span style={styles.statIcon}>{stat.icon}</span>
                <span style={styles.statValue}>{stat.value}</span>
                <span style={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Management Panel Section */}
        <div style={styles.managementSection}>
          <h2 style={styles.managementTitle}>{t('administration', 'Administration')}</h2>
          <p style={styles.managementText}>{t('adminDescription', 'For system administrators and management staff')}</p>
          <Link 
            to="/management-panel" 
            style={styles.managementButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 6px 25px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
            }}
          >
            <span style={styles.buttonIcon}>⚙️</span>
            <span style={styles.buttonText}>{t('managementPanel', 'Management Panel')}</span>
            <small style={styles.buttonSubtext}>({t('adminAccess', 'Admin & Management Access')})</small>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={styles.footerText}>
          © {new Date().getFullYear()} {t('companyName', 'Rajdoot Explorations')}. {t('allRightsReserved', 'All rights reserved.')}
        </p>
        <div style={styles.footerLinks}>
          <Link 
            to="/privacy" 
            style={styles.footerLink}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
          >
            {t('privacyPolicy', 'Privacy Policy')}
          </Link>
          <Link 
            to="/terms" 
            style={styles.footerLink}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
          >
            {t('termsOfService', 'Terms of Service')}
          </Link>
          <Link 
            to="/contact" 
            style={styles.footerLink}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
          >
            {t('contactUs', 'Contact Us')}
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default UserDashboard;